import React, { useState, useEffect } from 'react';
import {
 TrophyIcon,
 StarIcon,
 FireIcon,
 BoltIcon,
 ClockIcon,
 UserGroupIcon,
 AcademicCapIcon,
 SparklesIcon,
 GiftIcon,
 RocketLaunchIcon
} from '@heroicons/react/24/outline';
import {
 TrophyIcon as TrophyIconSolid,
 StarIcon as StarIconSolid,
 FireIcon as FireIconSolid,
 BoltIcon as BoltIconSolid,
 ClockIcon as ClockIconSolid,
 UserGroupIcon as UserGroupIconSolid,
 AcademicCapIcon as AcademicCapIconSolid,
 SparklesIcon as SparklesIconSolid,
 GiftIcon as GiftIconSolid,
 RocketLaunchIcon as RocketLaunchIconSolid
} from '@heroicons/react/24/solid';

const AchievementSystem = ({ userId, userProgress, dashboardType }) => {
 const [achievements, setAchievements] = useState([]);
 const [unlockedAchievements, setUnlockedAchievements] = useState([]);
 const [recentUnlocks, setRecentUnlocks] = useState([]);
 const [loading, setLoading] = useState(true);

 // Define all possible achievements
 const allAchievements = [
 // Basic Achievements
 {
 id: 'first_task',
 name: 'First Steps',
 description: 'Complete your first task',
 icon: <RocketLaunchIconSolid className="h-6 w-6" />,
 iconOutline: <RocketLaunchIcon className="h-6 w-6" />,
 category: 'basic',
 rarity: 'common',
 requirement: { type: 'tasks_completed', value: 1 },
 reward: { type: 'points', value: 10 },
 color: 'text-green-400',
 bgColor: 'bg-green-500/20',
 borderColor: 'border-green-500/30'
 },
 {
 id: 'week_warrior',
 name: 'Week Warrior',
 description: 'Maintain a 7-day learning streak',
 icon: <FireIconSolid className="h-6 w-6" />,
 iconOutline: <FireIcon className="h-6 w-6" />,
 category: 'streak',
 rarity: 'uncommon',
 requirement: { type: 'streak', value: 7 },
 reward: { type: 'points', value: 50 },
 color: 'text-orange-400',
 bgColor: 'bg-orange-500/20',
 borderColor: 'border-orange-500/30'
 },
 {
 id: 'speed_demon',
 name: 'Speed Demon',
 description: 'Complete 5 tasks in one day',
 icon: <BoltIconSolid className="h-6 w-6" />,
 iconOutline: <BoltIcon className="h-6 w-6" />,
 category: 'speed',
 rarity: 'rare',
 requirement: { type: 'daily_tasks', value: 5 },
 reward: { type: 'points', value: 100 },
 color: 'text-primary',
 bgColor: 'bg-secondary/20',
 borderColor: 'border-secondary/30'
 },
 {
 id: 'perfectionist',
 name: 'Perfectionist',
 description: 'Achieve 100% accuracy on 10 consecutive tasks',
 icon: <StarIconSolid className="h-6 w-6" />,
 iconOutline: <StarIcon className="h-6 w-6" />,
 category: 'accuracy',
 rarity: 'epic',
 requirement: { type: 'perfect_streak', value: 10 },
 reward: { type: 'points', value: 200 },
 color: 'text-purple-400',
 bgColor: 'bg-purple-500/20',
 borderColor: 'border-purple-500/30'
 },
 {
 id: 'century_club',
 name: 'Century Club',
 description: 'Complete 100 tasks',
 icon: <TrophyIconSolid className="h-6 w-6" />,
 iconOutline: <TrophyIcon className="h-6 w-6" />,
 category: 'milestone',
 rarity: 'legendary',
 requirement: { type: 'tasks_completed', value: 100 },
 reward: { type: 'certificate', value: 'premium' },
 color: 'text-primary',
 bgColor: 'bg-secondary/20',
 borderColor: 'border-secondary/30'
 },
 {
 id: 'early_bird',
 name: 'Early Bird',
 description: 'Complete a task before 8 AM',
 icon: <ClockIconSolid className="h-6 w-6" />,
 iconOutline: <ClockIcon className="h-6 w-6" />,
 category: 'time',
 rarity: 'common',
 requirement: { type: 'early_completion', value: 1 },
 reward: { type: 'points', value: 25 },
 color: 'text-blue-400',
 bgColor: 'bg-blue-500/20',
 borderColor: 'border-blue-500/30'
 },
 {
 id: 'team_player',
 name: 'Team Player',
 description: 'Complete 20 team tasks',
 icon: <UserGroupIconSolid className="h-6 w-6" />,
 iconOutline: <UserGroupIcon className="h-6 w-6" />,
 category: 'team',
 rarity: 'uncommon',
 requirement: { type: 'team_tasks', value: 20 },
 reward: { type: 'points', value: 75 },
 color: 'text-green-400',
 bgColor: 'bg-green-500/20',
 borderColor: 'border-green-500/30'
 },
 {
 id: 'scholar',
 name: 'Scholar',
 description: 'Achieve 95% accuracy on 50 tasks',
 icon: <AcademicCapIconSolid className="h-6 w-6" />,
 iconOutline: <AcademicCapIcon className="h-6 w-6" />,
 category: 'accuracy',
 rarity: 'rare',
 requirement: { type: 'high_accuracy', value: 50 },
 reward: { type: 'points', value: 150 },
 color: 'text-indigo-400',
 bgColor: 'bg-indigo-500/20',
 borderColor: 'border-indigo-500/30'
 },
 {
 id: 'streak_master',
 name: 'Streak Master',
 description: 'Maintain a 30-day learning streak',
 icon: <FireIconSolid className="h-6 w-6" />,
 iconOutline: <FireIcon className="h-6 w-6" />,
 category: 'streak',
 rarity: 'epic',
 requirement: { type: 'streak', value: 30 },
 reward: { type: 'points', value: 300 },
 color: 'text-red-400',
 bgColor: 'bg-red-500/20',
 borderColor: 'border-red-500/30'
 },
 {
 id: 'legend',
 name: 'Legend',
 description: 'Complete all tasks in your track',
 icon: <SparklesIconSolid className="h-6 w-6" />,
 iconOutline: <SparklesIcon className="h-6 w-6" />,
 category: 'milestone',
 rarity: 'legendary',
 requirement: { type: 'all_tasks', value: 1 },
 reward: { type: 'certificate', value: 'master' },
 color: 'text-pink-400',
 bgColor: 'bg-pink-500/20',
 borderColor: 'border-pink-500/30'
 }
 ];

 useEffect(() => {
 calculateAchievements();
 }, [userId, userProgress]);

 const calculateAchievements = async () => {
 try {
 setLoading(true);
 
 // Get user's progress data
 const userData = await getUserProgress(userId);
 
 // Check which achievements are unlocked
 const unlocked = allAchievements.filter(achievement => 
 checkAchievementRequirement(achievement, userData)
 );
 
 // Get recently unlocked achievements (last 7 days)
 const recent = await getRecentlyUnlockedAchievements(userId);
 
 setUnlockedAchievements(unlocked);
 setRecentUnlocks(recent);
 setAchievements(allAchievements);
 } catch (error) {
 console.error('Error calculating achievements:', error);
 } finally {
 setLoading(false);
 }
 };

 const getUserProgress = async (userId) => {
 // Mock user progress data - in real implementation, fetch from API
 return {
 tasksCompleted: userProgress?.completedTasks || 0,
 currentStreak: Math.floor(Math.random() * 15) + 1,
 longestStreak: Math.floor(Math.random() * 30) + 5,
 dailyTasks: Math.floor(Math.random() * 8),
 perfectStreak: Math.floor(Math.random() * 15),
 teamTasks: Math.floor(Math.random() * 25),
 highAccuracyTasks: Math.floor(Math.random() * 60),
 earlyCompletions: Math.floor(Math.random() * 10),
 totalTasks: userProgress?.totalTasks || 100
 };
 };

 const checkAchievementRequirement = (achievement, userData) => {
 const { type, value } = achievement.requirement;
 
 switch (type) {
 case 'tasks_completed':
 return userData.tasksCompleted >= value;
 case 'streak':
 return userData.currentStreak >= value;
 case 'daily_tasks':
 return userData.dailyTasks >= value;
 case 'perfect_streak':
 return userData.perfectStreak >= value;
 case 'team_tasks':
 return userData.teamTasks >= value;
 case 'high_accuracy':
 return userData.highAccuracyTasks >= value;
 case 'early_completion':
 return userData.earlyCompletions >= value;
 case 'all_tasks':
 return userData.tasksCompleted >= userData.totalTasks;
 default:
 return false;
 }
 };

 const getRecentlyUnlockedAchievements = async (userId) => {
 // Mock recent unlocks - in real implementation, fetch from API
 return unlockedAchievements.slice(0, 3).map(achievement => ({
 ...achievement,
 unlockedAt: new Date(Date.now() - Math.random() * 7 * 24 * 60 * 60 * 1000)
 }));
 };

 const getRarityColor = (rarity) => {
 switch (rarity) {
 case 'common': return 'text-muted';
 case 'uncommon': return 'text-green-400';
 case 'rare': return 'text-blue-400';
 case 'epic': return 'text-purple-400';
 case 'legendary': return 'text-primary';
 default: return 'text-muted';
 }
 };

 const getRarityBorder = (rarity) => {
 switch (rarity) {
 case 'common': return 'border-gray-500/30';
 case 'uncommon': return 'border-green-500/30';
 case 'rare': return 'border-blue-500/30';
 case 'epic': return 'border-purple-500/30';
 case 'legendary': return 'border-secondary/30';
 default: return 'border-gray-500/30';
 }
 };

 const getProgressPercentage = (achievement, userData) => {
 const { type, value } = achievement.requirement;
 let current = 0;
 let targetValue = value;
 
 switch (type) {
 case 'tasks_completed':
 current = userData.tasksCompleted;
 break;
 case 'streak':
 current = userData.currentStreak;
 break;
 case 'daily_tasks':
 current = userData.dailyTasks;
 break;
 case 'perfect_streak':
 current = userData.perfectStreak;
 break;
 case 'team_tasks':
 current = userData.teamTasks;
 break;
 case 'high_accuracy':
 current = userData.highAccuracyTasks;
 break;
 case 'early_completion':
 current = userData.earlyCompletions;
 break;
 case 'all_tasks':
 current = userData.tasksCompleted;
 targetValue = userData.totalTasks;
 break;
 default:
 current = 0;
 }
 
 return Math.min((current / targetValue) * 100, 100);
 };

 if (loading) {
 return (
 <div className="bg-gradient-to-br from-gray-800 to-gray-900 rounded-xl p-6 border border-gray-700">
 <div className="animate-pulse">
 <div className="h-6 bg-gray-700 rounded mb-4"></div>
 <div className="grid grid-cols-2 gap-4">
 {[1, 2, 3, 4].map(i => (
 <div key={i} className="h-24 bg-gray-700 rounded"></div>
 ))}
 </div>
 </div>
 </div>
 );
 }

 const unlockedCount = unlockedAchievements.length;
 const totalCount = achievements.length;

 return (
 <div className="bg-gradient-to-br from-gray-800 to-gray-900 rounded-xl p-6 border border-gray-700">
 {/* Header */}
 <div className="flex items-center justify-between mb-6">
 <div className="flex items-center gap-3">
 <div className="p-2 bg-secondary/20 rounded-lg">
 <TrophyIcon className="h-6 w-6 text-primary" />
 </div>
 <div>
 <h3 className="text-lg font-bold text-white">Achievements</h3>
 <p className="text-sm text-muted">{unlockedCount}/{totalCount} unlocked</p>
 </div>
 </div>
 <div className="text-right">
 <div className="text-2xl font-bold text-primary">{unlockedCount}</div>
 <div className="text-xs text-gray-500">badges earned</div>
 </div>
 </div>

 {/* Progress Bar */}
 <div className="mb-6">
 <div className="flex justify-between text-sm mb-2">
 <span className="text-muted">Progress</span>
 <span className="text-primary">{Math.round((unlockedCount / totalCount) * 100)}%</span>
 </div>
 <div className="w-full bg-gray-700 rounded-full h-2">
 <div 
 className="bg-gradient-to-r from-primary to-secondary h-2 rounded-full transition-all duration-500"
 style={{ width: `${(unlockedCount / totalCount) * 100}%` }}
 ></div>
 </div>
 </div>

 {/* Recent Unlocks */}
 {recentUnlocks.length > 0 && (
 <div className="mb-6">
 <h4 className="text-sm font-medium text-white mb-3 flex items-center gap-2">
 <GiftIcon className="h-4 w-4 text-green-400" />
 Recently Unlocked
 </h4>
 <div className="space-y-2">
 {recentUnlocks.map(achievement => (
 <div 
 key={achievement.id}
 className={`flex items-center gap-3 p-3 rounded-lg ${achievement.bgColor} ${achievement.borderColor} border`}
 >
 <div className={achievement.color}>
 {achievement.icon}
 </div>
 <div className="flex-1">
 <div className={`text-sm font-medium ${achievement.color}`}>
 {achievement.name}
 </div>
 <div className="text-xs text-muted">
 {achievement.description}
 </div>
 </div>
 <div className="text-xs text-gray-500">
 {achievement.unlockedAt.toLocaleDateString()}
 </div>
 </div>
 ))}
 </div>
 </div>
 )}

 {/* Achievement Grid */}
 <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
 {achievements.map(achievement => {
 const isUnlocked = unlockedAchievements.some(u => u.id === achievement.id);
 const userData = { tasksCompleted: userProgress?.completedTasks || 0, currentStreak: 5, dailyTasks: 2, perfectStreak: 3, teamTasks: 10, highAccuracyTasks: 25, earlyCompletions: 2, totalTasks: userProgress?.totalTasks || 100 };
 const progress = getProgressPercentage(achievement, userData);
 
 return (
 <div 
 key={achievement.id}
 className={`relative p-4 rounded-lg border transition-all duration-300 ${
 isUnlocked 
 ? `${achievement.bgColor} ${achievement.borderColor} border` 
 : 'bg-gray-700/30 border-gray-600/30 opacity-60'
 }`}
 >
 {/* Rarity indicator */}
 <div className={`absolute top-2 right-2 text-xs font-medium ${getRarityColor(achievement.rarity)}`}>
 {achievement.rarity}
 </div>
 
 {/* Achievement icon */}
 <div className={`mb-3 ${isUnlocked ? achievement.color : 'text-gray-500'}`}>
 {isUnlocked ? achievement.icon : achievement.iconOutline}
 </div>
 
 {/* Achievement details */}
 <div className="mb-3">
 <h4 className={`text-sm font-medium ${isUnlocked ? 'text-white' : 'text-muted'}`}>
 {achievement.name}
 </h4>
 <p className={`text-xs ${isUnlocked ? 'text-muted' : 'text-gray-500'}`}>
 {achievement.description}
 </p>
 </div>
 
 {/* Progress bar for locked achievements */}
 {!isUnlocked && (
 <div className="mb-2">
 <div className="flex justify-between text-xs text-gray-500 mb-1">
 <span>Progress</span>
 <span>{Math.round(progress)}%</span>
 </div>
 <div className="w-full bg-gray-600 rounded-full h-1">
 <div 
 className="bg-gray-500 h-1 rounded-full transition-all duration-300"
 style={{ width: `${progress}%` }}
 ></div>
 </div>
 </div>
 )}
 
 {/* Reward indicator */}
 {isUnlocked && (
 <div className="text-xs text-green-400 font-medium">
 +{achievement.reward.value} {achievement.reward.type}
 </div>
 )}
 </div>
 );
 })}
 </div>
 </div>
 );
};

export default AchievementSystem;



